import { useMemo, Fragment, memo, useState, useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import useAlerts from '@/hooks/useAlerts';
import useTheme from '@mui/styles/useTheme';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useScrimSectionStyles } from '@/features/scrims/styles/ScrimSection.styles';
import { useDispatch } from 'react-redux';

// components
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import AdminArea from '@/features/admin/components/AdminArea';
import MessengerButton from '@/features/messenger/components/MessengerButton';
import AdminPlayerControls from './AdminPlayerControls';

// utils
import 'moment-timezone';
import { copyTextToClipboard } from '@/utils/copyToClipboard';
import { truncate } from '@/utils/truncate';
import { getTeamBackgroundColor } from '@/utils/scrimMisc';
import { dateFromUTC, getUserTimezoneAbbr } from '@/utils/timezone';

// icons
import ShareIcon from '@mui/icons-material/Share';
import SettingsIcon from '@mui/icons-material/Settings';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { ROLE_IMAGES } from '@/utils/imageMaps';
import { FaGlobeEurope, FaGlobeAmericas, FaGlobeAsia } from 'react-icons/fa';
import { GiEarthAsiaOceania } from 'react-icons/gi';

// services
import { findScrimConversation } from '@/features/messenger/services/conversations.services';
import useScrimStore from '@/features/scrims/stores/scrimStore';

// Helper function to get region icon and color
const getRegionConfig = (region) => {
  const configs = {
    NA: { icon: FaGlobeAmericas, label: 'North America', color: '#4CAF50' },
    EUW: { icon: FaGlobeEurope, label: 'Europe West', color: '#2196F3' },
    EUNE: {
      icon: FaGlobeEurope,
      label: 'Europe Nordic & East',
      color: '#00BCD4',
    },
    KR: { icon: FaGlobeAsia, label: 'Korea', color: '#FF5722' },
    JP: { icon: FaGlobeAsia, label: 'Japan', color: '#E91E63' },
    OCE: { icon: GiEarthAsiaOceania, label: 'Oceania', color: '#9C27B0' },
    BR: { icon: FaGlobeAmericas, label: 'Brazil', color: '#FFC107' },
    LAS: {
      icon: FaGlobeAmericas,
      label: 'Latin America South',
      color: '#FF9800',
    },
    LAN: {
      icon: FaGlobeAmericas,
      label: 'Latin America North',
      color: '#FF6B35',
    },
    RU: { icon: FaGlobeEurope, label: 'Russia', color: '#673AB7' },
    TR: { icon: FaGlobeEurope, label: 'Turkey', color: '#F44336' },
    SG: { icon: FaGlobeAsia, label: 'Singapore', color: '#3F51B5' },
    TH: { icon: FaGlobeAsia, label: 'Thailand', color: '#009688' },
    TW: { icon: FaGlobeAsia, label: 'Taiwan', color: '#795548' },
    VN: { icon: FaGlobeAsia, label: 'Vietnam', color: '#607D8B' },
    PH: { icon: FaGlobeAsia, label: 'Philippines', color: '#CDDC39' },
  };

  return (
    configs[region] || {
      icon: FaGlobeAmericas,
      label: region,
      color: '#9E9E9E',
    }
  );
};

export default function ScrimSectionHeader({
  // props passed from ScrimSection.jsx
  scrim,
  setScrim,
  socket,
  gameEnded,
  casterEntered,
  joinCast,
  leaveCast,
  buttonsDisabled,
  isBoxExpanded,
  isInDetail,
}) {
  const { casters } = scrim;
  const { setCurrentAlert } = useAlerts();
  const classes = useScrimSectionStyles({ isBoxExpanded });
  const history = useHistory();
  const theme = useTheme();

  const regionConfig = getRegionConfig(scrim.region);
  const dispatch = useDispatch();
  const matchesMd = useMediaQuery(theme.breakpoints.down('md'));

  // Get unread scrim message count and helper functions
  const unreadScrimMessageCount = useScrimStore(
    (state) => state.unreadScrimMessages[scrim._conversation] || 0
  );
  const markScrimMessagesAsRead = useScrimStore(
    (state) => state.markScrimMessagesAsRead
  );
  const setScrimChatOpen = useScrimStore((state) => state.setScrimChatOpen);
  const showPlayers = useMediaQuery('(min-width:1000px)');

  const [isHover, setIsHover] = useState(false);

  // Chat open/close state is now handled globally in the scrim store

  const gameUrl = useMemo(
    () => `${window.location.origin}/scrims/${scrim._id}`,
    [scrim._id]
  );

  const handleOpenConversation = useCallback(async () => {
    try {
      const conversation = await findScrimConversation(scrim._id);

      // Mark scrim messages as read and chat as open
      markScrimMessagesAsRead(scrim._conversation);
      setScrimChatOpen(scrim._conversation, true);

      dispatch({
        type: 'general/scrimChatRoomOpen',
        payload: {
          conversation,
          isOpen: true,
          scrimId: scrim._id,
          extraTitle: scrim.title,
        },
      });
      return;
    } catch (error) {
      setCurrentAlert({
        type: 'Error',
        message: 'Error opening chat for scrim',
      });
      return;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrim._conversation, markScrimMessagesAsRead]);

  return (
    <Grid
      id="parent"
      container
      direction="column"
      flexWrap="nowrap"
      className={classes.scrimSectionHeader}>
      <Grid
        item
        container
        direction="row"
        alignItems="center"
        justifyContent="space-between">
        <Grid item sm={6}>
          <Grid container alignItems="center" spacing={1}>
            <Grid item>
              <Tooltip
                arrow
                placement="top"
                title="Redirect to scrim page"
                open={!isInDetail && isHover}>
                <Link
                  onMouseEnter={() => setIsHover(true)}
                  onMouseLeave={() => setIsHover(false)}
                  className="link"
                  style={{
                    textDecorationColor: '#000',
                    display: 'flex',
                    alignItems: 'center',
                    width: 'fit-content',
                  }}
                  to={`/scrims/${scrim._id}`}>
                  <Typography variant="h1" style={{ fontSize: '1.5rem' }}>
                    {/* if scrim has a title show title, else show createdby.name's scrim */}
                    {scrim.title ?? `${scrim.createdBy.name}'s Scrim`}
                  </Typography>
                </Link>
              </Tooltip>
            </Grid>
            <Grid item>
              <Tooltip title={regionConfig.label} arrow placement="top">
                <Chip
                  icon={<regionConfig.icon style={{ fontSize: '1.2rem' }} />}
                  label={scrim.region}
                  size="medium"
                  sx={{
                    backgroundColor: `${regionConfig.color}20`,
                    border: `1px solid ${regionConfig.color}60`,
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    height: '32px',
                    '& .MuiChip-icon': {
                      color: regionConfig.color,
                    },
                    '&:hover': {
                      backgroundColor: `${regionConfig.color}30`,
                      border: `1px solid ${regionConfig.color}80`,
                    },
                  }}
                />
              </Tooltip>
            </Grid>
            {scrim.isPrivate && (
              <Grid item>
                <Chip
                  label="Private"
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    color: '#999',
                    fontWeight: 500,
                  }}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid
          item
          container
          sm={8}
          md={6}
          alignItems="center"
          direction="row"
          justifyContent={matchesMd ? 'flex-start' : 'flex-end'}
          spacing={2}>
          {/* messenger button */}
          {scrim._conversation && (
            <Grid item>
              <MessengerButton
                withDropdown={false}
                onClick={handleOpenConversation}
                tooltipTitle="Open scrim chat room"
                tooltipType="secondary"
                isScrim={true}
                scrimUnreadCount={unreadScrimMessageCount}
              />
            </Grid>
          )}

          {/* Share button */}
          <Grid item>
            <Tooltip arrow placement="top" title="Copy game link to clipboard">
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setCurrentAlert({
                    type: 'Success',
                    message: 'copied game link to clipboard!',
                  });
                  copyTextToClipboard(gameUrl);
                }}>
                <ShareIcon /> Share
              </Button>
            </Tooltip>
          </Grid>

          <AdminArea>
            {/* Edit and close event buttons */}
            <Grid item>
              <Tooltip
                arrow
                placement="top"
                title="Admin only: edit game details (name, password, etc...)">
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => history.push(`/scrims/${scrim._id}/edit`)}>
                  <SettingsIcon />
                  &nbsp; Edit
                </Button>
              </Tooltip>
            </Grid>

            <Grid item>
              <AdminPlayerControls scrim={scrim} />
            </Grid>
          </AdminArea>
        </Grid>
      </Grid>

      <Grid container direction="row" justifyContent="space-between">
        <Grid item xs={6}>
          <Box
            sx={(theme) => ({
              position: 'relative',
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.primary.light, 0.08)} 100%)`,
              backdropFilter: 'blur(10px)',
              borderRadius: '12px',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              p: 2,
              mb: 1,
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.2)}`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                  'linear-gradient(45deg, transparent 0%, rgba(255, 255, 255, 0.03) 50%, transparent 100%)',
                animation: 'shimmer 3s ease-in-out infinite',
              },
              '@keyframes shimmer': {
                '0%': { transform: 'translateX(-100%)' },
                '100%': { transform: 'translateX(100%)' },
              },
            })}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                position: 'relative',
                zIndex: 1,
              }}>
              <Box
                sx={(theme) => ({
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                  boxShadow: `0 4px 15px ${alpha(theme.palette.primary.main, 0.4)}`,
                })}>
                <ScheduleIcon sx={(theme) => ({ color: theme.palette.primary.contrastText, fontSize: '1.5rem' })} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    mb: 0.5,
                  }}>
                  Game Start Time
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}>
                  <CalendarTodayIcon
                    sx={(theme) => ({
                      fontSize: '1rem',
                      color: theme.palette.primary.light,
                    })}
                  />
                  {dateFromUTC(scrim.gameStartTime).format('MMM DD, YYYY')}
                  <Box
                    sx={{
                      width: '4px',
                      height: '4px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255, 255, 255, 0.5)',
                    }}
                  />
                  <AccessTimeIcon
                    sx={(theme) => ({
                      fontSize: '1rem',
                      color: theme.palette.primary.light,
                    })}
                  />
                  {dateFromUTC(scrim.gameStartTime).format('h:mm A')}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontSize: '0.75rem',
                    fontWeight: 400,
                    mt: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}>
                  {getUserTimezoneAbbr()}
                  <Box
                    sx={{
                      width: '2px',
                      height: '2px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255, 255, 255, 0.4)',
                    }}
                  />
                  {dateFromUTC(scrim.gameStartTime).fromNow()}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>

        {!isBoxExpanded && showPlayers && (
          <Grid item xs={4}>
            <Typography
              style={{ marginRight: '10px' }}
              variant="h2"
              textAlign="center">
              {[...scrim.teamOne, ...scrim.teamTwo].length
                ? 'Players:'
                : 'No players'}
            </Typography>
          </Grid>
        )}
      </Grid>

      <Grid
        container
        direction="row"
        justifyContent={scrim.isWithCasters ? 'space-between' : 'flex-end'}>
        {/*  casters text and buttons*/}
        {scrim.isWithCasters && (
          <CastersSection
            showCasters={isInDetail ? true : showPlayers || isBoxExpanded}
            casters={casters}
            gameEnded={gameEnded}
            data={{
              maxCastersAllowedCount: scrim.maxCastersAllowedCount ?? 2, // didn't exist in old versions of db
              casterEntered,
              casters,
              buttonsDisabled,
              joinCast,
              leaveCast,
              isBoxExpanded,
            }}
          />
        )}

        {/* dont show players in header if isBoxExpanded or smaller screen */}
        {!isBoxExpanded && showPlayers && (
          <Grid
            item
            container
            xs={6}
            spacing={2}
            direction="row"
            justifyContent="flex-end">
            <OneTeam
              teamName="teamOne"
              winnerTeam={scrim?.teamWon || null}
              teamArr={scrim.teamOne}
              teamRoles={['Top', 'Jungle', 'Mid', 'ADC', 'Support']}
              alignContent="flex-end"
            />

            <OneTeam
              teamName="teamTwo"
              winnerTeam={scrim?.teamWon || null}
              teamArr={scrim.teamTwo}
              alignContent="flex-start"
              teamRoles={['Top', 'Jungle', 'Mid', 'ADC', 'Support']}
            />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
}

const CastersSection = memo(
  ({ casters, gameEnded, data, showCasters }) =>
    showCasters && (
      <Grid container direction="column" item xs={6}>
        {casters.length === 2 ? (
          <Typography variant="h2">
            Casters:{' '}
            {casters.map((caster, idx) => (
              <Fragment key={caster._id}>
                <Tooltip
                  arrow
                  placement="top"
                  title={`visit ${caster?.name}'s profile`}>
                  <Link
                    className="link"
                    to={`/users/${caster?.name}?region=${caster?.region}`}>
                    {caster?.name}
                  </Link>
                </Tooltip>
                {idx === 0 ? ' & ' : ''}
              </Fragment>
            ))}
          </Typography>
        ) : (
          <Grid item container direction="column" alignItems="flex-start">
            {casters.length === 0 ? (
              <Typography variant="h2">No Casters</Typography>
            ) : null}
            {casters[0] && (
              <Typography variant="h2">
                {/* if game didn't and say current casters, else say one caster: */}
                {!gameEnded ? 'Current Casters:' : 'Caster:'}&nbsp;
                <Tooltip
                  arrow
                  placement="top"
                  title={`visit ${casters[0]?.name}'s profile`}>
                  <Link
                    className="link"
                    to={`/users/${casters[0]?.name}?region=${casters[0]?.region}`}>
                    {casters[0].name}
                  </Link>
                </Tooltip>
              </Typography>
            )}
          </Grid>
        )}

        <JoinCastButtons data={data} gameEnded={gameEnded} />
      </Grid>
    )
);

const JoinCastButtons = memo(({ data, gameEnded }) => {
  const {
    casterEntered,
    casters,
    buttonsDisabled,
    joinCast,
    leaveCast,
    maxCastersAllowedCount,
  } = data;

  // // don't show cast buttons if game ended or we have max casters or currentUser has joined cast
  if (gameEnded) return null;

  return (
    <Grid container alignItems="center" direction="row" spacing={2}>
      {casters.length !== maxCastersAllowedCount && !casterEntered && (
        <Grid item>
          <Button
            variant="contained"
            color="primary"
            disabled={
              casters.length === maxCastersAllowedCount ||
              casterEntered ||
              buttonsDisabled
            }
            onClick={joinCast}>
            join cast
          </Button>
        </Grid>
      )}

      {casterEntered && (
        <Grid item>
          <Button
            color="secondary"
            variant="contained"
            disabled={buttonsDisabled}
            onClick={leaveCast}>
            Leave cast
          </Button>
        </Grid>
      )}
    </Grid>
  );
});

const OneTeam = memo(
  ({ teamArr, teamRoles, alignContent, teamName, winnerTeam }) => {
    const background = getTeamBackgroundColor(teamName, winnerTeam);

    return (
      <Grid
        item
        container
        direction="column"
        style={{
          alignContent,
          backgroundColor: background.normal,
          backgroundImage: background.gradient,
          paddingTop: '10px',
          paddingBottom: '10px',
        }}
        flexWrap="nowrap"
        alignItems="flex-start"
        xs={4}>
        {teamRoles.map((teamRole) => {
          const playerAssigned = teamArr.find(
            (player) => player?.role === teamRole
          );

          if (!playerAssigned) return null;

          const userInfo = playerAssigned?._user ?? null;

          if (!userInfo) return null;

          return (
            <Grid item key={userInfo?._id} xs={10}>
              <Tooltip
                arrow
                placement="top"
                title={`Visit ${userInfo?.name}'s profile`}>
                <Link
                  className={'link'}
                  to={`/users/${userInfo?.name}?region=${userInfo?.region}`}>
                  <img
                    alt={playerAssigned?.role}
                    src={ROLE_IMAGES[teamRole]}
                    style={{
                      position: 'relative',
                      top: '5px',
                      width: '20px',
                      marginRight: '2px',
                    }}
                  />
                  {truncate(userInfo.name, 12)}
                </Link>
              </Tooltip>
            </Grid>
          );
        })}
      </Grid>
    );
  }
);
